import Phaser from 'phaser';
import type { TableId } from '@casino/core';

const COPY: Partial<Record<TableId, { title: string; subtitle: string }>> = {
  'craps:high-limit':     { title: 'HIGH LIMIT UNLOCKED', subtitle: 'Visit the floor' },
  'craps:whale':          { title: 'SALON PRIVÉ OPEN',    subtitle: 'Visit the floor' },
};

/**
 * Slide-in / hold / slide-out tier-unlock banner. Renders at the top of the
 * scene's camera. Non-blocking — round play continues underneath.
 *
 * @param scene UI scene that owns the toast (deals with depth).
 * @param tableId Identifies which copy to show; non-tier tableIds are ignored.
 * @param dpr Device pixel ratio multiplier.
 */
export function showTierUnlockToast(
  scene: Phaser.Scene,
  tableId: TableId,
  dpr: number,
): void {
  const copy = COPY[tableId];
  if (!copy) return;
  const W = scene.scale.width;
  const cx = W / 2;
  const px = (n: number) => Math.round(n * dpr);
  const targetY = px(70);
  const startY = -px(80);

  const bg = scene.add.graphics().setDepth(1000);
  const drawBg = (y: number) => {
    bg.clear();
    bg.fillStyle(0x1a0e08, 0.95);
    bg.fillRoundedRect(cx - px(180), y, px(360), px(70), px(8));
    bg.lineStyle(px(2), 0xd4af37, 1);
    bg.strokeRoundedRect(cx - px(180), y, px(360), px(70), px(8));
  };
  drawBg(startY);

  const title = scene.add.text(cx, startY + px(20), copy.title, {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: `${px(20)}px`,
    color: '#f4d35e',
    fontStyle: 'bold',
  }).setOrigin(0.5).setDepth(1001);

  const subtitle = scene.add.text(cx, startY + px(48), copy.subtitle, {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    fontSize: `${px(12)}px`,
    color: '#d4af37',
  }).setOrigin(0.5).setDepth(1001);

  const proxy = { y: startY };
  scene.tweens.add({
    targets: proxy,
    y: targetY,
    duration: 220,
    ease: 'Cubic.Out',
    onUpdate: () => {
      drawBg(proxy.y);
      title.setY(proxy.y + px(20));
      subtitle.setY(proxy.y + px(48));
    },
    onComplete: () => {
      scene.time.delayedCall(2400, () => {
        scene.tweens.add({
          targets: proxy,
          y: startY,
          duration: 220,
          ease: 'Cubic.In',
          onUpdate: () => {
            drawBg(proxy.y);
            title.setY(proxy.y + px(20));
            subtitle.setY(proxy.y + px(48));
          },
          onComplete: () => {
            bg.destroy();
            title.destroy();
            subtitle.destroy();
          },
        });
      });
    },
  });
}
